import { IPanelItem } from '@se/cp.common';
import { DataBinding } from '@se/se.platform.base';
import { every, times } from 'lodash-es';
import { cpControlFactory, CpControlType } from '../view-model';
import { isNotNullOrUndefined, ROW_HEIGHT, ROW_PADDING } from './utils';

export interface CpControlGridCell {
    alignBottom: boolean;
    control: CpControlType | null;
    invalidatedBy: CpControlType | null;
    minHeight: number;
}

export interface CpControlGridRow {
    cells: ReadonlyArray<CpControlGridCell>;
    flexible: boolean;
    height?: number;
}



export class CpControlGrid {
    private readonly rows: CpControlGridRow[];

    /**
     * La creazione avviene in tre passi:
     *   - Viene inizializzata la griglia con celle vuote.
     *   - I controlli vengono posizionati nella griglia, invalidando le celle che vengono "coperte" a causa
     *       del columnSpan o rowSpan del controllo.
     *   - Vengono impostati altezze ed allineamenti.
     */
    static fromPanel(panel: IPanelItem, dataBindingContext?: DataBinding) {
        const numCols = panel.controlPanel.columns || 1;
        const numRows = panel.controlPanel.rows || 1;

        if (!panel.controlPanel || !panel.controlPanel.controls || panel.controlPanel.controls.length === 0) {
            return CpControlGrid.empty(numRows, numCols);
        }

        const grid = new CpControlGrid(numRows, numCols);

        for (const control of panel.controlPanel.controls) {
            // Costruisci il controllo
            const built = cpControlFactory(control, dataBindingContext);

            for (let x = 0; x < control.rowSpan; ++x) {
                for (let y = 0; y < control.columnSpan; ++y) {
                    // Invalida tutte le celle occupate dal controllo.
                    grid.setInvalidatedBy(control.row + x, control.column + y, built);
                }
            }

            // Assegna il controllo alla griglia.
            grid.setControlAt(control.row, control.column, built);
        }

        grid.setLayoutStyles();

        return grid;
    }

    static empty(declaredNumRows = 0, declaredNumColumns = 0) {
        return new CpControlGrid(0, 0, declaredNumRows, declaredNumColumns);
    }

    /**
     * Costruisce la griglia con un dato numero di righe e colonne vuote.
     */
    private constructor(
        readonly numRows: number,
        readonly numColumns: number,
        readonly declaredNumRows = numRows,
        readonly declaredNumColumns = numColumns,
    ) {
        this.rows = times(numRows, () => ({
            cells: times(numColumns, () => ({
                alignBottom: false,
                control: null,
                controlHeight: 0,
                invalidatedBy: null,
                minHeight: 0,
            })),
            flexible: true,
            height: undefined,
        }));
    }

    isEmpty() {
        for (const row of this.rows) {
            for (const cell of row.cells) {
                if (cell.control) {
                    return false;
                }
            }
        }

        return true;
    }

    getAllControls(): CpControlType[] {
        return this.rows
            .map((row) => row.cells.filter(isNotNullOrUndefined))
            .flat()
            .map((cell) => cell.control)
            .filter(isNotNullOrUndefined);
    }

    /**
     * @returns La lista appiattita dei controlli non invalidati.
     */
    toValidArray(): CpControlGridRow[] {
        return this.rows.map((row: CpControlGridRow) => {
            const hasControl = !!row.cells.find((c) => c.control);
            // Se la riga non ha controlli, rimuoviamo tutte le celle, perché è inutile disegnarle.
            const cells = hasControl ? row.cells.filter((cell) => !cell.invalidatedBy) : [];
            const height = row.height ?? 0;

            return {
                cells,
                height,
                flexible: row.flexible,
            };
        });
    }

    /**
     * Assegna un controllo ad una cella.
     */
    private setControlAt(x: number, y: number, control: CpControlType) {
        const row = this.rows[x];
        row.flexible = false;

        const cell = row.cells[y];
        cell.control = control;
        cell.invalidatedBy = null;
    }

    /**
     * Marca una cella come appartenente ad un controllo con span > 1.
     */
    private setInvalidatedBy(x: number, y: number, control: CpControlType) {
        this.rows[x].cells[y].invalidatedBy = control;
    }

    /**
     * Calcolo altezza di più righe:
     *  - Le righe sono flessibili, ossia hanno altezza modificabile, se almeno una delle righe è flessibile.
     *      Questo avviene solitamente se non ci sono controlli in quella riga.
     *  - Per ogni riga:
     *      - Se l'altezza è già stata calcolata, usa quella.
     *      - Se è da calcolare, usa l'altezza massima dei controlli a span 1 di quella riga.
     * - L'altezza totale delle righe è la somma dell'altezza delle righe singole più il padding di ogni riga oltre la prima.
     */
    private getRowHeights(rowIdx: number, span: number) {
        let sum = 0;
        let isFlexible = false;

        for (let x = rowIdx; x < rowIdx + span; ++x) {
            const row = this.rows[x];
            if (!row) {
                continue;
            }
            if (row.flexible) {
                isFlexible = true;
                continue;
            }
            if (row.height !== undefined) {
                sum += row.height;
                continue;
            }

            let max = 0;
            for (const cell of row.cells) {
                if (cell.control && cell.control.rowSpan === 1) {
                    max = Math.max(max, cell.control.getHeightHint());
                }
            }

            if (max === 0) {
                row.height = ROW_HEIGHT;
            } else {
                row.height = max;
            }

            sum += max;
        }

        // Margini tra i controlli.
        sum += 2 * (span - 1) * ROW_PADDING;

        return {
            height: sum,
            isFlexible,
        };
    }

    private fixRowHeights(rowIdx: number, span: number, targetHeight: number) {
        const flexibleRows: CpControlGridRow[] = [];

        for (let x = rowIdx; x < rowIdx + span; ++x) {
            const row = this.rows[x];
            if (!row) {
                continue;
            }
            if (row.flexible) {
                flexibleRows.push(row);
            } else {
                targetHeight -= row.height ?? 0;
            }
        }

        if (flexibleRows.length === 0) {
            // Errore?
            return;
        }

        // Margini tra i controlli.
        const padding = 2 * ROW_PADDING * flexibleRows.length;
        targetHeight -= padding;

        for (const row of flexibleRows) {
            row.height = targetHeight / flexibleRows.length;
            row.flexible = false;
        }
    }

    /**
     * Vedi commenti nei metodi sotto.
     */
    private setLayoutStyles() {
        const cellsByRowSpan = this.groupCellsByRowSpan();

        // Aggiungi le righe per i controlli troppo grandi.
        for (let i = 2; i < cellsByRowSpan.length; ++i) {
            const cells = cellsByRowSpan[i];
            if (!cells) {
                continue;
            }
            for (const cell of cells) {
                this.addPaddingIfNeeded(cell);
            }
        }
    }

    /**
     * 1 Per ogni riga, se tutti i controlli sono allineabili in basso, imposta l'allineamento della riga in basso.
     *
     * 2 Itera suddividendo per rowSpan
     *     a b c d e      { 1: a b c e f g i j l m n o p q r s t
     *     f f g d h      {
     *     i j k d h  ->  { 2: h k
     *     l m k n o      {
     *     p q r s t      { 3: d
     */
    private groupCellsByRowSpan() {
        const cellsByRowSpan: CpControlGridCell[][] = [];

        for (const row of this.rows) {
            let canAlignToBottom = every(row.cells, (cell) => cell.control?.canAlignToBottom() ?? true);

            for (const cell of row.cells) {
                // 1.
                if (cell.invalidatedBy || !cell.control) {
                    continue;
                }
                cell.alignBottom = canAlignToBottom;
                // 2.
                const span = cell.control.rowSpan;
                if (!cellsByRowSpan[span]) {
                    cellsByRowSpan[span] = [];
                }
                cellsByRowSpan[span].push(cell);
            }
        }

        if (cellsByRowSpan[1]) {
            this.assignCellHeights(cellsByRowSpan[1]);
        }

        return cellsByRowSpan;
    }

    private assignCellHeights(oneRowSpanCells: CpControlGridCell[]) {
        for (const cell of oneRowSpanCells) {
            if (cell.control) {
                const height = cell.control.getHeightHint();
                cell.control.setHeight(height);
                cell.minHeight = height;
            }
        }
    }

    /**
     * 3 Per ogni gruppo con rowSpan > 1, imposta l'altezza come constraint tra l'altezza del controllo e la somma dei figli
     *
     * 4 Per ogni rowSpan > 1:
     *     4.1 Se l'altezza del controllo è maggiore di quella dei figli di 10px, inserisci una riga vuota ed aggiorna il rowspan di tutti i controlli su quella riga.
     *     4.2 Se l'altezza del controllo è maggiore di quella dei figli di meno di 10px, rimpicciolisci il controllo
     *     4.2 Se l'altezza del controllo è minore di quella dei figli, ingrandisci il controllo
     */
    private addPaddingIfNeeded(cell: CpControlGridCell) {
        // 3
        if (cell.control) {
            const controlHeight = cell.control.getHeightHint();
            const { height: childrenHeight, isFlexible } = this.getRowHeights(cell.control.row, cell.control.rowSpan);

            if (isFlexible) {
                this.fixRowHeights(cell.control.row, cell.control.rowSpan, controlHeight);
                cell.control.setHeight(controlHeight);
                cell.minHeight = controlHeight;
            } else {
                // 4.2, 4.3
                cell.control.setHeight(childrenHeight);
                cell.minHeight = childrenHeight;
            }
        }
    }
}
